import React, { useState, useReducer, useCallback, useEffect } from 'react'
import { Button, Modal, Stepper } from '@enamelai/design-system'
import './AddPatient.css'
import BackgroundStep from './BackgroundStep'
import FormsStep from './FormStep'
import PatientReview from './PatientReview'
import PersonalInfoStep from './PersonalInfoStep'
import { Patient } from '@dataTypes/PatientInfo.types'
import { reducer, initialState } from '@store/reducers/addPatient.reducer'
import { useAppSelector } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { logger } from '@utils/safeLogger'
import { defaultPatientData } from './patientDataDefaults'
import {
  normalizePatientForDraft,
  normalizePatientForSave,
  validatePatientForDraft,
  validatePatientStep,
} from './patientForm.logic'

const STEPS = ['Personal', 'Background', 'Forms', 'Review']
const REVIEW_STEP = STEPS.length

type AddPatientModalProps = {
  isOpen: boolean
  onRequestClose: () => void
  onSave: (patientData: Patient) => Promise<void> | void
  patientData?: Patient | null
  /** Accepted for react-bootstrap modal call sites; the DS modal has its own overlay. */
  backdropClassName?: string
  customClassName?: string
}

const AddPatientModal: React.FC<AddPatientModalProps> = ({
  isOpen,
  onRequestClose,
  onSave,
  patientData: initialPatientData,
  customClassName,
}) => {
  const [step, setStep] = useState<number>(1)
  const [state, localDispatch] = useReducer(reducer, initialState)
  const selectedClinic = useAppSelector(
    (rootState) => rootState.clinic.selectedClinic,
  )
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  useEffect(() => {
    localDispatch({
      type: 'RESET',
      payload: initialPatientData
        ? {
            ...initialPatientData,
            uploadedFiles: [],
            allergy_ids:
              initialPatientData.allergy_ids ??
              initialPatientData.patient_allergies?.map((a) => a.allergy_id) ??
              [],
            medical_condition_ids:
              initialPatientData.medical_condition_ids ??
              initialPatientData.patient_medical_conditions?.map(
                (c) => c.medical_condition_id,
              ) ??
              [],
          }
        : defaultPatientData,
    })
  }, [initialPatientData])

  useEffect(() => {
    if (!isOpen) {
      setStep(1)
      localDispatch({ type: 'CLEAR_ERRORS' })
    }
  }, [isOpen])

  const handleInputChange = useCallback(
    (
      field: keyof Patient,
      value: string | number | boolean | string[] | File[] | undefined,
    ): void => {
      localDispatch({ type: 'SET_FIELD', field, value })
    },
    [localDispatch],
  )

  const handleFileUpload = (files: FileList | null): void => {
    if (files) {
      localDispatch({ type: 'ADD_FILE', files: Array.from(files) })
    }
  }

  const handleDeleteFile = (index: number): void => {
    localDispatch({ type: 'DELETE_FILE', index })
  }

  const validateStep = (): boolean => {
    const newErrors = validatePatientStep(state.patientData, step === 2 ? 2 : 1)

    localDispatch({ type: 'SET_ERRORS', errors: newErrors })

    return Object.keys(newErrors).length === 0
  }

  const handleNext = (): void => {
    if (validateStep()) {
      setStep(step + 1)
    }
  }

  const handleSave = async (): Promise<void> => {
    showLoading({
      container: '.add-patient-modal',
      hideElements: ['patient-table-container .patient-content'],
    })
    const clinicId = state.patientData.patient_id
      ? state.patientData.clinic_id
      : selectedClinic?.clinic_id || state.patientData.clinic_id

    const updatedPatientData = normalizePatientForSave(
      state.patientData,
      clinicId,
    )

    try {
      await onSave(updatedPatientData)
      setTimeout(() => {
        hideLoading('.add-patient-modal')
        onRequestClose()
        showToast(
          createSuccessToast(
            'Patient saved successfully',
            'Patient has been saved successfully.',
          ),
        )
      }, 2000)
    } catch (e) {
      logger.error('Error saving patient:', e)
      setTimeout(() => {
        hideLoading('.add-patient-modal')
        showToast(
          createFailedToast(
            'Patient not saved',
            'Patient not saved. Please try again.',
          ),
        )
      }, 2000)
    }
  }

  const handleSaveDraft = async (): Promise<void> => {
    const newErrors = validatePatientForDraft(state.patientData)
    localDispatch({ type: 'SET_ERRORS', errors: newErrors })
    if (Object.keys(newErrors).length > 0) return

    const clinicId = state.patientData.patient_id
      ? state.patientData.clinic_id
      : selectedClinic?.clinic_id || state.patientData.clinic_id
    const draft: Patient = normalizePatientForDraft(state.patientData, clinicId)
    showLoading({
      container: '.add-patient-modal',
      hideElements: ['patient-table-container .patient-content'],
    })
    try {
      await onSave(draft)
      setTimeout(() => {
        hideLoading('.add-patient-modal')
        onRequestClose()
        showToast(
          createSuccessToast(
            'Draft saved',
            'The patient was saved as a draft.',
          ),
        )
      }, 2000)
    } catch (e) {
      logger.error('Error saving draft patient:', e)
      setTimeout(() => {
        hideLoading('.add-patient-modal')
        showToast(
          createFailedToast(
            'Draft not saved',
            'Draft not saved. Please try again.',
          ),
        )
      }, 2000)
    }
  }

  const renderStepContent = (): JSX.Element | null => {
    switch (step) {
      case 1:
        return (
          <PersonalInfoStep
            patientData={state.patientData}
            handleInputChange={handleInputChange}
            validationErrors={state.validationErrors}
          />
        )
      case 2:
        return (
          <BackgroundStep
            patientData={state.patientData}
            handleInputChange={handleInputChange}
            validationErrors={state.validationErrors}
          />
        )
      case 3:
        return (
          <FormsStep
            patientData={state.patientData}
            handleInputChange={handleInputChange}
            handleFileUpload={handleFileUpload}
            handleDeleteFile={handleDeleteFile}
          />
        )
      case 4:
        return <PatientReview patientData={state.patientData} />
      default:
        return null
    }
  }

  const isReviewStep = step === REVIEW_STEP
  const canSkip = step > 1 && !isReviewStep

  return (
    <Modal
      open={isOpen}
      onClose={onRequestClose}
      size="md"
      title={state.patientData.patient_id ? 'Edit patient' : 'Add patient'}
      closeOnBackdrop={false}
      className={['add-patient-modal', customClassName]
        .filter(Boolean)
        .join(' ')}
      bodyClassName="add-patient-modal__body"
      primaryLabel={isReviewStep ? 'Save' : 'Next'}
      onPrimaryClick={isReviewStep ? handleSave : handleNext}
      secondaryLabel={step > 1 ? 'Previous' : ''}
      onSecondaryClick={() => setStep(step - 1)}
      footerStart={
        canSkip || (step < 4 && state.patientData.patient_status === 34) ? (
          <>
            {step < 4 && state.patientData.patient_status === 34 && (
              <Button variant="ghost" onClick={handleSaveDraft}>
                Save as draft
              </Button>
            )}
            {canSkip && (
              <Button
                variant="tertiary"
                size="md"
                onClick={() => setStep(step + 1)}
              >
                Skip
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      <Stepper
        className="add-patient-modal__stepper"
        steps={STEPS}
        currentStep={step - 1}
      />
      <div className="add-patient-modal__content">{renderStepContent()}</div>
    </Modal>
  )
}

export default AddPatientModal
